import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchLeaderboard, LeaderboardEntry, getPersonalBest, syncOfflineSubmissions, forceSyncLocalData, updatePlayerNameInLeaderboard } from '../utils/leaderboard';
import { getPlayerId, getPlayerName, setPlayerName } from '../utils/playerId';
import { getLeaderboardApiUrl } from '../utils/env';

interface LeaderboardProps {
  isOpen: boolean;
  onClose: () => void;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ isOpen, onClose }) => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [playerRank, setPlayerRank] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [personalBest, setPersonalBest] = useState<LeaderboardEntry | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [playerName, setPlayerNameState] = useState(getPlayerName());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: number; failed: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadLeaderboard();
      
      // When leaderboard opens, try to sync any pending submissions
      syncOfflineSubmissions();
      
      // Also listen for online event to sync when connection is restored
      const handleOnline = () => {
        syncOfflineSubmissions();
        loadLeaderboard(); // Refresh leaderboard after syncing
      };
      
      window.addEventListener('online', handleOnline);
      
      return () => {
        window.removeEventListener('online', handleOnline);
      };
    }
  }, [isOpen]);

  const loadLeaderboard = async () => {
    setIsLoading(true);
    try {
      const data = await fetchLeaderboard(50);
      setEntries(data.entries);
      setPlayerRank(data.playerRank);
      
      const best = getPersonalBest();
      setPersonalBest(best);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameSave = async () => {
    const trimmedName = playerName.trim() || getPlayerName();
    setPlayerName(trimmedName);
    setIsEditingName(false);
    
    // Update name in leaderboard (local and remote)
    try {
      await updatePlayerNameInLeaderboard(trimmedName);
    } catch (error) {
      console.error('Failed to update player name in leaderboard:', error);
    }
    
    // Reload leaderboard to show updated name
    await loadLeaderboard();
  };

  const handleForceSync = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    
    try {
      const result = await forceSyncLocalData();
      setSyncResult({ success: result.success, failed: result.failed });
      
      if (result.success > 0) {
        // Reload leaderboard after successful sync
        setTimeout(() => {
          loadLeaderboard();
        }, 1000);
      }
      
      if (result.errors.length > 0) {
        console.warn('Sync errors:', result.errors);
      }
    } catch (error) {
      console.error('Force sync failed:', error);
      setSyncResult({ success: 0, failed: 1 });
    } finally {
      setIsSyncing(false);
    }
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getRankEmoji = (rank: number): string => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return '';
  };

  const currentPlayerId = getPlayerId();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="leaderboard-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      >
        <motion.div
          className="leaderboard-modal"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.3, bounce: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="leaderboard-header">
            <h2>🏆 Leaderboard</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

          {/* Player Name Section */}
          <div className="player-name-section">
            {isEditingName ? (
              <div className="name-editor">
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerNameState(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleNameSave();
                    if (e.key === 'Escape') {
                      setIsEditingName(false);
                      setPlayerNameState(getPlayerName());
                    }
                  }}
                  maxLength={20}
                  placeholder="Enter your name"
                  autoFocus
                />
                <button onClick={handleNameSave}>Save</button>
                <button onClick={() => {
                  setIsEditingName(false);
                  setPlayerNameState(getPlayerName());
                }}>Cancel</button>
              </div>
            ) : (
              <div className="player-info">
                <span>Playing as: <strong>{playerName}</strong></span>
                <button
                  className="edit-name-button"
                  onClick={() => setIsEditingName(true)}
                  title="Edit name"
                >
                  ✏️
                </button>
              </div>
            )}
          </div>

          {/* Personal Best */}
          {personalBest && (
            <div className="personal-best">
              <div className="personal-best-label">Your Best:</div>
              <div className="personal-best-score">
                Score: {personalBest.score.toLocaleString()} | Level: {personalBest.level} | Words: {personalBest.wordsFound}
              </div>
            </div>
          )}

          {/* Player Rank */}
          {playerRank && (
            <div className="player-rank">
              Your Rank: <strong>#{playerRank}</strong>
            </div>
          )}

          {/* Leaderboard List */}
          <div className="leaderboard-list">
            {isLoading ? (
              <div className="leaderboard-loading">Loading leaderboard...</div>
            ) : entries.length === 0 ? (
              <div className="leaderboard-empty">
                No scores yet. Complete a level to see your score here!
              </div>
            ) : (
              <div className="leaderboard-entries">
                {entries.map((entry, index) => {
                  const rank = index + 1;
                  const isCurrentPlayer = entry.playerId === currentPlayerId;
                  
                  return (
                    <motion.div
                      key={`${entry.playerId}-${entry.timestamp}`}
                      className={`leaderboard-entry ${isCurrentPlayer ? 'current-player' : ''}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02 }}
                    >
                      <div className="entry-rank">
                        {getRankEmoji(rank)}
                        <span className="rank-number">{rank}</span>
                      </div>
                      <div className="entry-name">
                        {entry.playerName}
                        {entry.isLocal ? (
                          <span className="local-badge" title="Local score">💾</span>
                        ) : (
                          <span className="global-badge" title="Global score">🌐</span>
                        )}
                      </div>
                      <div className="entry-stats">
                        <span className="entry-score">{entry.score.toLocaleString()}</span>
                        <span className="entry-level">Lvl {entry.level}</span>
                        <span className="entry-time">{formatTimestamp(entry.timestamp)}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="leaderboard-footer">
            <p className="leaderboard-note">
              {entries.length > 0 && entries[0].isLocal ? (
                <>
                  💾 Local leaderboard - scores stored in your browser
                  <br />
                  <small style={{ fontSize: '10px', opacity: 0.7 }}>
                    To enable global leaderboard, set REACT_APP_LEADERBOARD_API_URL in production
                  </small>
                </>
              ) : (
                <>🌐 Global leaderboard - competing with players worldwide</>
              )}
            </p>
            
            {/* Force Sync Button - show if API is configured */}
            {getLeaderboardApiUrl() && (
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <motion.button
                  className="sync-button"
                  onClick={handleForceSync}
                  disabled={isSyncing}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isSyncing ? '🔄 Syncing...' : '📤 Sync Local Data to Server'}
                </motion.button>
                {syncResult && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: syncResult.failed > 0 ? '#f44336' : '#4CAF50' }}>
                    {syncResult.success > 0 && `✅ ${syncResult.success} synced`}
                    {syncResult.failed > 0 && ` ❌ ${syncResult.failed} failed`}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Leaderboard;

